import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Chip,
  Stack,
  Avatar,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Business,
  Handshake,
  TrendingUp,
  Security,
  ArrowForward,
  CheckCircle,
  Close,
  Email,
  Person,
  Phone,
  Send,
  Router,
  CellTower,
  Public,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CTASection from '../components/common/CTASection';

const Partners = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    partnerType: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const partnerPrograms = [
    { 
      icon: <Business />, 
      title: 'Technology Partners', 
      desc: 'Integrate your solutions with our global network', 
      benefits: ['API Access', 'Joint Marketing', 'Technical Support'],
      color: '#e60108',
    },
    { 
      icon: <Handshake />, 
      title: 'Reseller Partners', 
      desc: 'Sell our services to your customers', 
      benefits: ['Competitive Margins', 'Sales Training', 'Marketing Materials'],
      color: '#191919',
    },
    { 
      icon: <TrendingUp />, 
      title: 'Strategic Partners', 
      desc: 'Long-term collaboration on major projects', 
      benefits: ['Dedicated Account Manager', 'Custom Solutions', 'Priority Support'],
      color: '#e60108',
    },
    { 
      icon: <Security />, 
      title: 'Referral Partners', 
      desc: 'Earn commissions for referring clients', 
      benefits: ['Recurring Commission', 'Easy Tracking', 'Quick Payouts'],
      color: '#a0a0a0',
    },
  ];

  const partnerLogos = [
    '/images/Our Partners.jpg',
    '/images/Our Partners.jpg',
    '/images/Our Partners.jpg',
    '/images/Our Partners.jpg',
    '/images/Our Partners.jpg',
    '/images/Our Partners.jpg',
    '/images/Our Partners.jpg',
    '/images/Our Partners.jpg',
  ];

  // Updated stats to match the correct data
  const stats = [
    { number: '50,000+', label: 'KM of Fiber Network', icon: <Router />, color: '#e60108' },
    { number: '100+', label: 'Telecom Towers', icon: <CellTower />, color: '#191919' },
    { number: '15+', label: 'Countries Served', icon: <Public />, color: '#e60108' },
    { number: '100%', label: 'Uptime Guarantee', icon: <CheckCircle />, color: '#191919' },
  ];

  const handleApplyClick = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({ name: '', email: '', phone: '', company: '', partnerType: '' });
  };

  const handleSubmit = () => {
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      handleCloseDialog();
    }, 3000);
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: '50vh',
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/images/Our Partners.jpg"
          alt="Our Partners"
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 100%)',
            zIndex: 1,
          }}
        />
        
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Chip
              label="Partnership"
              sx={{
                bgcolor: '#e60108',
                color: '#fefefe',
                mb: 3,
                fontWeight: 600,
                letterSpacing: '0.5px',
              }}
            />
            <Typography
              variant="h1"
              sx={{
                color: '#fefefe',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 800,
                lineHeight: 1.2,
                textShadow: '0 2px 20px rgba(0,0,0,0.3)',
              }}
            >
              Our Partners
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(254,254,254,0.9)',
                maxWidth: 700,
                mx: 'auto',
                fontSize: '1.25rem',
                lineHeight: 1.6,
              }}
            >
              Collaborating with industry leaders to deliver excellence across 5+ countries worldwide
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Stats Section with Future Goal Note */}
      <Box sx={{ py: 6, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <Grid container spacing={3} justifyContent="center">
            {stats.map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                    <Avatar sx={{ bgcolor: stat.color + '15', color: stat.color, margin: '0 auto 16px', width: 60, height: 60 }}>
                      {stat.icon}
                    </Avatar>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: stat.color, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                      {stat.label}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Single Future Goal Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
          >
            <Paper
              elevation={0}
              sx={{
                mt: 5,
                p: 3,
                bgcolor: '#f8f8f8',
                borderRadius: 3,
                textAlign: 'center',
                maxWidth: 700,
                mx: 'auto',
                border: '1px solid #e6010820',
              }}
            >
              <Typography variant="body1" sx={{ color: '#191919' }}>
                <Box component="span" sx={{ color: '#e60108', fontWeight: 700 }}>🎯 Future Goal:</Box> These statistics represent our ambitious targets for the coming years as we continue to expand and enhance our global communication infrastructure.
              </Typography>
            </Paper>
          </motion.div>
        </Container>
      </Box>

      {/* Partner Programs */}
      <Box sx={{ py: 8, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Chip
              label="Partner Programs"
              sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600, mx: 'auto', display: 'table' }}
            />
            <Typography variant="h2" align="center" sx={{ mb: 2, fontWeight: 800, color: '#191919' }}>
              Partner Programs
            </Typography>
            <Typography variant="h6" align="center" sx={{ color: '#a0a0a0', mb: 6, maxWidth: 700, mx: 'auto' }}>
              Join our global partner ecosystem and grow your business with us
            </Typography>
          </motion.div>

          <Grid container spacing={3} justifyContent="center">
            {partnerPrograms.map((program, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -8 }}
                >
                  <Card sx={{ height: '100%', p: 3, textAlign: 'center', borderRadius: 3 }}>
                    <Avatar
                      sx={{
                        width: 70,
                        height: 70,
                        bgcolor: program.color + '15',
                        color: program.color,
                        margin: '0 auto 16px',
                      }}
                    >
                      {program.icon}
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#191919' }}>
                      {program.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 2 }}>
                      {program.desc}
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 2 }}>
                      {program.benefits.map((benefit, idx) => (
                        <Stack direction="row" spacing={1} alignItems="center" key={idx}>
                          <CheckCircle sx={{ fontSize: 16, color: program.color }} />
                          <Typography variant="caption" sx={{ color: '#191919' }}>{benefit}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Current Partners */}
      <Box sx={{ py: 8, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Chip
              label="Our Network"
              sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600, mx: 'auto', display: 'table' }}
            />
            <Typography variant="h2" align="center" sx={{ mb: 2, fontWeight: 800, color: '#191919' }}>
              Trusted by Industry Leaders
            </Typography>
            <Typography variant="h6" align="center" sx={{ color: '#a0a0a0', mb: 6 }}>
              Join our growing network of partners worldwide
            </Typography>
          </motion.div>

          <Grid container spacing={3} justifyContent="center">
            {partnerLogos.map((logo, index) => (
              <Grid item xs={6} sm={4} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                >
                  <Paper
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 120,
                      bgcolor: '#fefefe',
                      borderRadius: 3,
                      '&:hover': {
                        boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Box
                      component="img"
                      src={logo}
                      alt={`Partner ${index + 1}`}
                      sx={{ maxWidth: '100%', maxHeight: 80, objectFit: 'contain' }}
                    />
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why Partner With Us */}
      <Box sx={{ py: 8, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Chip
                  label="Why Partner With Us"
                  sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600 }}
                />
                <Typography variant="h2" sx={{ mb: 3, fontWeight: 800, color: '#191919' }}>
                  Grow Together, 
                  <br />
                  <Box component="span" sx={{ color: '#e60108' }}>Succeed Together</Box>
                </Typography>
                <Typography variant="body1" sx={{ color: '#a0a0a0', mb: 3, lineHeight: 1.8 }}>
                  Partnering with Worldwide Communication gives you access to our global network of 
                  50,000+ km fiber infrastructure, 100+ telecom towers, and presence across 5+ countries.
                </Typography>
                <Stack spacing={2}>
                  {[
                    'Access to 5+ countries market',
                    'Competitive commission structure',
                    'Dedicated partner manager',
                    'Marketing and sales support',
                    'Technical training and certification',
                    'Priority technical support',
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <CheckCircle sx={{ color: '#e60108' }} />
                        <Typography variant="body1" sx={{ color: '#191919' }}>{item}</Typography>
                      </Stack>
                    </motion.div>
                  ))}
                </Stack>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Box
                  component="img"
                  src="/images/Our Partners.jpg"
                  alt="Partnership"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Become a Partner CTA */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Card sx={{ p: 5, textAlign: 'center', borderRadius: 4, background: 'linear-gradient(135deg, #e60108 0%, #191919 100%)', color: '#fefefe' }}>
              <Typography variant="h3" gutterBottom fontWeight="800">
                Become a Partner
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
                Join our global partner ecosystem and grow your business with us
              </Typography>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={handleApplyClick}
                  sx={{
                    bgcolor: '#fefefe',
                    color: '#e60108',
                    px: 5,
                    py: 1.5,
                    '&:hover': {
                      bgcolor: '#fefefe',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Apply Now
                </Button>
              </motion.div>
            </Card>
          </motion.div>
        </Container>
      </Box>

      <CTASection 
        title="Let's Partner" 
        subtitle="Explore partnership opportunities with us"
        buttonText="Contact Partnership Team"
        buttonLink="/contact"
      />

      {/* Application Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#e60108', color: '#fefefe' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="bold">Partner Application</Typography>
            <IconButton onClick={handleCloseDialog} size="small" sx={{ color: '#fefefe' }}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Full Name"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              InputProps={{ startAdornment: <Person sx={{ mr: 1, color: '#a0a0a0' }} /> }}
            />
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              InputProps={{ startAdornment: <Email sx={{ mr: 1, color: '#a0a0a0' }} /> }}
            />
            <TextField
              fullWidth
              label="Phone Number"
              placeholder="Enter your phone number"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              InputProps={{ startAdornment: <Phone sx={{ mr: 1, color: '#a0a0a0' }} /> }}
            />
            <TextField
              fullWidth
              label="Company Name"
              placeholder="Enter your company name"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              InputProps={{ startAdornment: <Business sx={{ mr: 1, color: '#a0a0a0' }} /> }}
            />
            <TextField
              fullWidth
              select
              label="Partner Type"
              value={formData.partnerType}
              onChange={(e) => setFormData({ ...formData, partnerType: e.target.value })}
              SelectProps={{ native: true }}
            >
              <option value="">Select Partner Type</option>
              <option value="technology">Technology Partner</option>
              <option value="reseller">Reseller Partner</option>
              <option value="strategic">Strategic Partner</option>
              <option value="referral">Referral Partner</option>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleCloseDialog} variant="outlined" sx={{ borderColor: '#a0a0a0', color: '#a0a0a0' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            endIcon={<Send />}
            sx={{ bgcolor: '#e60108', '&:hover': { bgcolor: '#b80006' } }}
          >
            Submit Application
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar open={submitted} autoHideDuration={3000} onClose={() => setSubmitted(false)}>
        <Alert severity="success" sx={{ width: '100%', bgcolor: '#00C853', color: '#fefefe' }}>
          Application submitted successfully! We'll contact you soon.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Partners;